import React, { Fragment, useState, useEffect, useContext } from 'react';
import propTypes from 'prop-types';
import Images from 'assets/images/imageData';
import Layout from 'components/sales/layout/layout';
import DivLayout from 'components/sales/divLayout/divLayout';
import localization from 'assets/localization/localization';
import PanelCardWithHeading from 'components/tickets/panelCardWithHeading';
import DocumentView from 'components/sales/downloadDocView/documentView';
import ModalPop from 'components/sales/modal/modal';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ComplaintReplySupport from 'components/sales/ListFilters/ComplaintReplySupport';
import { toast } from 'react-toastify';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import DropDownInput from 'components/sales/Input/dropDownInput/dropDownInput';
import TextAreaInput from 'components/sales/Input/textAreaInput/textAreaInput';
import FileUpload from 'components/sales/fileUpload/fileUpload';
import useLatLng from 'hooks/uselatlng/uselatlng';
import DistanceCalculator from 'pages/DistanceCalculator/Calculator';
import Config from 'services/config/config';
import axios from 'services/interceptor/Interceptor';
import { isActiveDistanceCheckin } from 'services/attendance/isActiveDistanceCheckin';

const MODULE_TYPE = 'cte_ticket';

/** Redux may store one row (pickDistanceRow) or a list — normalize for CTE ticket match. */
const rowsFromDistanceData = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'object' && (data.id != null || data.task_id != null || data.ticket_id != null || data.lat != null)) {
    return [data];
  }
  return [];
};

const ticketRef = (row) => row?.ticket_id ?? row?.task_id;

const findActiveCheckinForTicket = (data, complainId) => {
  const rows = rowsFromDistanceData(data);
  return (
    rows.find((d) => {
      if (!isActiveDistanceCheckin(d)) return false;
      const ref = ticketRef(d);
      return ref != null && String(ref) === String(complainId);
    }) || null
  );
};

const checkinLogsForTicket = (data, complainId) => {
  const rows = rowsFromDistanceData(data);
  return rows.filter((d) => {
    const ref = ticketRef(d);
    return ref != null && String(ref) === String(complainId);
  });
};

const CentralTechViewIssue = ({
  complainRaisedTicket,
  reopenComplain,
  match,
  documentUploadUrlPublic,
  documentUrlDataPublic,
  history,
  saveAssigneeDetails,
  subIssueDropdown,
  getSubIssueDropdown = [],
  fetchCteIssueList,
  getAllIssuesListing = [],
  saveRaiseIssueSupport,
  saveRaiseIssueReply = [],
  saveCteComplainRemarks,
  cteComplainRemarks = [],
  calculateDistance,
  getDistanceData,
  getSiteData,
  siteData,
  rmCheckOut,
  distanceData = [],
}) => {
  const [listData, setListData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [complainId, setComplainId] = useState(false);
  const [isConfirmation, setIsConfirmation] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSuccessFor, setIsSuccessFor] = useState('');
  const [ticketData, setTicketData] = useState('');
  const [status, setStatus] = useState('');
  const [internalStatus, setInternalStatus] = useState(null);
  const [filterData, setFilterData] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [isReply, setIsReply] = useState(false);
  const { baseUrl } = useContext(BaseUrl);
  const [replyStatus, setReplyStatus] = useState(false);
  const [roleId, setRoleId] = useState('');
  const [remark, setRemark] = useState('');
  const [image, setImage] = useState('');
  const [siteId, setSiteId] = useState('');
  const [showRemarkModal, setShowRemarkModal] = useState(false);

  // PRD §2 — Check-in / check-out state.
  const islatlng = useLatLng();
  const [activeCheckin, setActiveCheckin] = useState(null); // current active row
  const [isCheckinInProgress, setIsCheckinInProgress] = useState(false);
  const [isCheckoutInProgress, setIsCheckoutInProgress] = useState(false);
  const [checkinLogs, setCheckinLogs] = useState([]);
  const [showCheckinPopup, setShowCheckinPopup] = useState(false);

  // Close ticket category state (same complaint_issue_type dropdowns as L2).
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [selectedIssueType, setSelectedIssueType] = useState(null);
  const [selectedSubIssueType, setSelectedSubIssueType] = useState(null);
  const [closeMessage, setCloseMessage] = useState('');
  const [closeAttachment, setCloseAttachment] = useState('');
  const [isCloseInProgress, setIsCloseInProgress] = useState(false);

  const compId = { complain_id: complainId };

  useEffect(() => {
    let roleId = localStorage.getItem('fusion-role');
    setRoleId(roleId);
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await fetchCteIssueList(null, 1, match.params.id, false);
      setIsLoading(false);
    })();
  }, [fetchCteIssueList, match.params.id]);

  // Load active check-in and logs when ticket data arrives.
  useEffect(() => {
    if (getAllIssuesListing !== null) {
      setTicketData(getAllIssuesListing);
      const sid = getAllIssuesListing?.resultSets?.[0]?.site_id;
      setSiteId(sid);
      setStatus(getAllIssuesListing?.status);
      setInternalStatus(getAllIssuesListing?.internal_status);
      // Fetch check-in data for this ticket once we know the site_id.
      if (sid && getDistanceData) {
        getDistanceData(sid, MODULE_TYPE, match.params.id);
      }
    }
  }, [getAllIssuesListing]);

  // Refresh active check-in from distanceData (Redux may be one object via pickDistanceRow).
  useEffect(() => {
    const logs = checkinLogsForTicket(distanceData, match.params.id);
    setCheckinLogs(logs);
    setActiveCheckin(findActiveCheckinForTicket(distanceData, match.params.id));
  }, [distanceData, match.params.id]);

  const refreshCheckinData = async () => {
    const resolvedSiteId = siteId || ticketData?.resultSets?.[0]?.site_id;
    if (getDistanceData && resolvedSiteId) {
      await getDistanceData(resolvedSiteId, MODULE_TYPE, match.params.id);
    }
  };

  // Haversine distance (same as siteStages).
  const calcCrow = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  };
  const toRad = (v) => (v * Math.PI) / 180;

  // PRD §2.1 — Open check-in popup (same DistanceCalculator popup as siteStages).
  const handleCheckin = () => {
    setShowCheckinPopup(true);
    toast.info('Opening check-in popup...');
  };

  // PRD §2.2 — Check-out handler (same pattern as siteStages handleCheckOut).
  const handleCheckout = async () => {
    if (isCheckoutInProgress || !activeCheckin) return;
    const checkout_lat = Number(islatlng?.lat);
    const checkout_long = Number(islatlng?.lng);
    if (isNaN(checkout_lat) || isNaN(checkout_long)) {
      alert(localization.locationPermission);
      return;
    }
    let checkout_distance = 0;
    if (siteData?.lead?.latitude && siteData?.lead?.longitude) {
      checkout_distance = calcCrow(
        checkout_lat,
        checkout_long,
        Number(siteData.lead.latitude),
        Number(siteData.lead.longitude)
      );
    }
    setIsCheckoutInProgress(true);
    try {
      const payload = {
        id: activeCheckin.id,
        checkout_lat: checkout_lat,
        checkout_long: checkout_long,
        checkout_distance_in_km: checkout_distance.toFixed(3),
        checkout_remark: '',
      };
      const url = `${Config.maintenance.rmCheckOut}`;
      const res = await axios.post(url, payload);
      const isSuccess =
        res?.data?.status === 0 ||
        res?.data?.response?.status === true ||
        res?.data?.response?.data === true;
      if (isSuccess) {
        toast.success('Checked out successfully');
        window.location.reload();
        return;
      }
      toast.error(res?.data?.message || 'Check-out failed. Please try again.');
    } catch (err) {
      toast.error('Check-out failed. Please try again.');
    } finally {
      setIsCheckoutInProgress(false);
    }
  };

  const handleIsConfirmation = () => setIsConfirmation(!isConfirmation);
  const handleIsSuccess = () => setIsSuccess(!isSuccess);
  const filterToggle = () => setShowFilter(!showFilter);

  const handleIsReply = () => {
    setIsReply(!isReply);
    history.push(`${baseUrl}/central-tech-complain-list`);
  };

  const handleCloseComplain = async () => {
    // PRD §3.3 — show category modal instead of immediately closing.
    setIsConfirmation(false);
    setShowCloseModal(true);
  };

  // PRD §3.3 — Submit close with category (Laravel: save-issue-reply only, isClosed always false).
  const submitCloseTicket = async () => {
    if (!closeMessage || closeMessage.trim() === '') {
      toast.error('Please enter message');
      return;
    }
    if (!selectedIssueType) {
      toast.error('Please select a closing category');
      return;
    }
    const hasSubTypes = getSubIssueDropdown?.data?.length > 0;
    if (hasSubTypes && !selectedSubIssueType) {
      toast.error('Please select a sub-category');
      return;
    }
    setIsCloseInProgress(true);
    try {
      const attachmentFile = closeAttachment
        ? (Array.isArray(closeAttachment) ? closeAttachment : [closeAttachment])
        : [];

      const closePayload = {
        message: closeMessage,
        send_to: '',
        attachment_mand: false,
        attachment_file: attachmentFile,
        isClosed: false,
        rca: '',
        issue_related_to: selectedIssueType.id,
        issue_related_to_sub_cat: selectedSubIssueType?.id || '',
      };

      if (activeCheckin) {
        const checkout_lat = Number(islatlng?.lat);
        const checkout_long = Number(islatlng?.lng);
        if (isNaN(checkout_lat) || isNaN(checkout_long)) {
          toast.error('Could not get your location');
          return;
        }
        closePayload.checkout_lat = String(checkout_lat);
        closePayload.checkout_long = String(checkout_long);
      }

      await saveRaiseIssueSupport(closePayload, match.params.id);

      setShowCloseModal(false);
      setCloseMessage('');
      setCloseAttachment('');
      setSelectedIssueType(null);
      setSelectedSubIssueType(null);
      setIsLoading(true);
      await fetchCteIssueList(null, 1, match.params.id, false);
      setIsLoading(false);
      toast.success('Ticket closed successfully');
      setIsSuccessFor(localization.closeComplain);
      handleIsSuccess();
    } catch (err) {
      toast.error('Failed to close ticket. Please try again.');
    } finally {
      setIsCloseInProgress(false);
    }
  };

  const handleContinue = () => {
    (async () => {
      if (remark === '') {
        toast.error('Remark is required');
        return;
      }
      const list = {};
      list['remarks'] = remark;
      list['doc'] = image;
      list['id'] = match?.params?.id;
      list['site_id'] = siteId;
      setIsLoading(true);
      const res = await saveCteComplainRemarks(list);
      if (res === true) {
        setIsLoading(false);
        toast.success("Remarks added successfully");
        window.location.reload();
      } else {
        setIsLoading(false);
      }
    })();
  };

  const handleReopen = (val) => {
    if (val) {
      (async () => {
        await reopenComplain(compId);
        setIsLoading(true);
        await fetchCteIssueList(match.params.id, 1, match.params.id, false);
        setIsLoading(false);
        setIsSuccessFor(localization.closeComplain);
        handleIsSuccess();
      })();
    }
  };

  const handleModalHide = () => {
    setIsLoading(true);
    setShowRemarkModal(false);
    setIsLoading(false);
    window.location.reload();
  };

  const successIconBox = () => (
    <div className="text-center">
      <div className="success">
        <img src={Images?.tick} alt="" />
      </div>
      <h5>
        {isSuccessFor === localization.raiseTicket
          ? localization.ticketSuccessfullyCreated
          : localization.complainClosedSuccessfully}
      </h5>
      <Button onClick={handleIsSuccess} className="btn btn-primary">
        {localization.continue} <img src={Images?.ArrowRight} alt="" />
      </Button>
    </div>
  );

  const applyFilter = async (filterData) => {
    if (filterData.message === '' || filterData.message == null) {
      toast.error('Please enter message.');
      return false;
    }
    setIsLoading(true);
    setFilterData(filterData);
    const res = await saveRaiseIssueSupport(filterData, match.params.id);
    setIsLoading(false);
    if (res) setIsReply(true);
    filterToggle();
  };

  const isClosed = getAllIssuesListing?.resultSets?.[0]?.internal_status === 'Closed';
  const issueDropdown = getAllIssuesListing?.issue_dropdown || [];

  const headerBtns = () => (
    <>
      {/* Add Remarks — always visible (Laravel staging parity, including closed tickets) */}
      <Fragment>
        <Button
          onClick={() => setShowRemarkModal(true)}
          className="filter-hamburger btn btn-transparent filter-btn-dir filters_direction filters-section"
        >
          <img src={Images?.SelectMenu} alt="" />
          <b className="site_filters" style={{ width: "max-content" }}>Add Remarks</b>
        </Button>
      </Fragment>

      {/* PRD §2.1 — Check-in / Check-out button */}
      {!isClosed && (
        <Fragment>
          {activeCheckin ? (
            <Button
              onClick={handleCheckout}
              disabled={isCheckoutInProgress}
              className="btn btn-warning ml-2"
            >
              {isCheckoutInProgress ? 'Checking out…' : 'Check Out'}
            </Button>
          ) : (
            <Button
              onClick={handleCheckin}
              disabled={isCheckinInProgress}
              className="btn btn-success ml-2"
            >
              {isCheckinInProgress ? 'Checking in…' : 'Check In'}
            </Button>
          )}
        </Fragment>
      )}

      {/* Close button visible only after active check-in */}
      {parseInt(roleId) === 61 && activeCheckin && !isClosed && (
        <Fragment>
          <Button
            onClick={handleCloseComplain}
            className="filter-hamburger btn btn-transparent filter-btn-dir filters_direction filters-section ml-2"
          >
            <img src={Images?.SelectMenu} alt="" />
            <b className="site_filters" style={{ width: "max-content" }}>Close</b>
          </Button>
        </Fragment>
      )}
    </>
  );

  return (
    <Fragment>
      {/* ── Check-in Popup (same as siteStages) ── */}
      {showCheckinPopup && (
        <div className="popup-box" style={{ zIndex: 99999 }}>
          <div className="box">
            <button
              className="btn btn-primary mb-4"
              style={{ cursor: 'pointer', float: 'right', border: '0px', boxShadow: 'none', position: 'relative', top: '-17px' }}
              onClick={() => { setShowCheckinPopup(false); refreshCheckinData(); }}
            >
              x
            </button>
            <DistanceCalculator
              siteData={siteData}
              getSiteData={getSiteData}
              calculateDistance={calculateDistance}
              siteId={ticketData?.resultSets?.[0]?.site_id || match.params.id}
              moduleType={MODULE_TYPE}
              task_id={parseInt(match.params.id)}
              onSuccess={async () => {
                setShowCheckinPopup(false);
                await refreshCheckinData();
                toast.success('Checked in successfully');
              }}
            />
          </div>
        </div>
      )}

      <Layout
        title={'View Ticket'}
        Buttons={headerBtns}
        isLoading={isLoading}
        isGoback={true}
        linkFlag
        linkUrl={'/dashboard/central-tech-complain-list'}
      >
        {/* ── Remarks Modal ── */}
        <ModalPop title="Remarks" show={showRemarkModal} handleModalHide={handleModalHide}>
          <div className="modal-body">
            <div className="text-center">
              <div className="form-group">
                <div className="col-sm-6">
                  <TextAreaInput
                    className="inline-css scrollbar-styling"
                    type="text"
                    rows={4}
                    label={localization.addRemark}
                    required
                    inputValue={remark}
                    value={remark}
                    isStarRequired={true}
                    handleChange={(e) => setRemark(e.target.value)}
                  />
                </div>
                <div className="col-sm-6">
                  <FileUpload
                    id="remarkDocUpload"
                    accept=".pdf,.png,.jpg"
                    documentUploadUrl={documentUploadUrlPublic}
                    documentUrlData={documentUrlDataPublic}
                    onChange={(val) => setImage(val)}
                    multiple={true}
                  >
                    <span className="btn btn-transparent">Upload Documents</span>
                  </FileUpload>
                </div>
              </div>
              <Button className="btn btn-primary" onClick={handleContinue} disabled={!remark}>
                {localization.continue} <img src="../images/arrow-right.png" alt="" />
              </Button>
            </div>
          </div>
        </ModalPop>

        {/* ── Ticket Details ── */}
        {ticketData && ticketData?.resultSets?.length > 0 && ticketData?.resultSets?.map((ticket) => (
          <Fragment key={ticket.id}>
            <PanelCardWithHeading title={'Ticket Details'} status={ticket?.internal_status}>
              <div className="details-listing">
                <DivLayout heading={'Ticket Id'} data={ticket?.id} />
                <DivLayout heading={'Raised By Name'} data={ticket?.user_name} />
                <DivLayout heading={'Raised By Email'} data={ticket?.user_email} />
                <DivLayout heading={'Raised By Mobile'} data={ticket?.phone_number} />
                <DivLayout heading={'Status'} data={ticket?.internal_status} />
                {ticket?.ticket_type && <DivLayout heading={'Ticket Type'} data={ticket?.ticket_type} />}
                <DivLayout heading={'Issue Description'} data={ticket?.complain_description} />
                <DivLayout heading={localization.createdOn} data={ticket?.created_at} />
                <DocumentView attachment={ticket?.attachment} key={document.id} />
              </div>
            </PanelCardWithHeading>

            {/* PRD §2.4 — Check-in / Check-out log */}
            {checkinLogs.length > 0 && (
              <PanelCardWithHeading title="Check-in / Check-out Log">
                <table className="panel-table table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Mode</th>
                      <th>Distance (km)</th>
                      <th>Actioned By</th>
                      <th>Checked In At</th>
                      <th>Checked Out At</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkinLogs.map((log, idx) => (
                      <tr key={log.id}>
                        <td>{idx + 1}</td>
                        <td>{log.eng_at}</td>
                        <td>{log.distance_in_km}</td>
                        <td>{log.actioned_by || log.checked_in_by || '–'}</td>
                        <td>{log.checkin_time || log.created_at}</td>
                        <td>{log.checkout_time || '–'}</td>
                        <td>
                          <span className={`badge ${isActiveDistanceCheckin(log) ? 'badge-success' : 'badge-secondary'}`}>
                            {isActiveDistanceCheckin(log) ? 'Active' : 'Done'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </PanelCardWithHeading>
            )}

            {/* Remarks */}
            {ticket?.messages?.length > 0 && ticket?.messages?.map((message, index) => (
              <PanelCardWithHeading title={`Remarks #${ticket?.messages?.length - index}`} key={index}>
                <div className="details-listing">
                  {message?.sent_from_name && <DivLayout heading="Message From" data={message?.sent_from_name} />}
                  {message?.message && <DivLayout heading="Remarks" data={message?.message} />}
                  {message?.attachment && message?.attachment?.length > 0 && message?.attachment?.map((att, i) => (
                    <DocumentView attachment={att} key={i + 1} showFullWidth={false} />
                  ))}
                  {message?.created_at && (
                    <DivLayout
                      heading="Created At"
                      data={new Date(message?.created_at).toISOString().replace('T', ' ').slice(0, 19)}
                    />
                  )}
                </div>
              </PanelCardWithHeading>
            ))}
          </Fragment>
        ))}

        {/* Message thread */}
        {ticketData?.messageData?.map((messageDatas) => (
          <PanelCardWithHeading
            key={messageDatas?.id}
            title={`Replied By: ${messageDatas?.message_from} (${messageDatas?.created_at})`}
            colorChange={messageDatas?.message_from === 'Client' && '#fff3f3'}
          >
            <div className="details-listing">
              <DivLayout heading="Send To" data={messageDatas?.send_to} />
              {messageDatas?.rm_ticket_id ? (
                <DivLayout heading="Ticket Id" data={messageDatas?.rm_ticket_id} />
              ) : (
                <DivLayout heading="Created By" data={messageDatas?.name || 'Client'} />
              )}
              {messageDatas?.document?.map((doc) => <DocumentView key={doc} attachment={doc} />)}
              <DivLayout heading="Message" data={messageDatas?.message} />
            </div>
          </PanelCardWithHeading>
        ))}

        {/* Reply section — only visible when actively checked in to this ticket */}
        {activeCheckin && !isClosed && (
          <ComplaintReplySupport
            showFilter={showFilter}
            filterToggle={filterToggle}
            viewDropdown={getAllIssuesListing}
            subIssueDropdown={subIssueDropdown}
            getSubIssueDropdown={getSubIssueDropdown}
            internalStatus={internalStatus}
            applyFilter={(val) => applyFilter(val)}
            statusId="1,2,3"
            documentUploadUrlPublic={documentUploadUrlPublic}
            documentUrlDataPublic={documentUrlDataPublic}
            source="LeadX"
          />
        )}
      </Layout>

      {/* ── PRD §3.3 — Close Ticket Category Modal ── */}
      <ModalPop
        title="Close Ticket"
        show={showCloseModal}
        handleModalHide={() => setShowCloseModal(false)}
      >
        <div className="modal-body">
          <div className="row">
            <div className="col-12 mb-3">
              <DropDownInput
                label={localization.issueRelatedTo}
                data={issueDropdown}
                getIdValue={(val) => {
                  if (!val?.id) return;
                  setSelectedIssueType(val);
                  setSelectedSubIssueType(null);
                  subIssueDropdown(val.id);
                }}
                getValueById={selectedIssueType?.id}
                isStarRequired
              />
            </div>
            <div className="col-12 mb-3">
              <TextAreaInput
                className="inline-css scrollbar-styling"
                type="text"
                rows={3}
                label={localization.message}
                required
                isStarRequired
                inputValue={closeMessage}
                value={closeMessage}
                handleChange={(e) => setCloseMessage(e.target.value)}
              />
            </div>
            <div className="col-12 mb-3">
              <FileUpload
                id="closeTicketAttachmentUpload"
                accept=".pdf,.png,.jpg,.jpeg,.doc"
                documentUploadUrl={documentUploadUrlPublic}
                documentUrlData={documentUrlDataPublic}
                onChange={(val) => setCloseAttachment(val)}
                multiple={true}
              >
                <span className="btn btn-transparent">{localization.attachment}</span>
              </FileUpload>
            </div>
            {selectedIssueType?.id &&
              getSubIssueDropdown?.data?.length > 0 && (
              <div className="col-12 mb-3">
                <DropDownInput
                  key={selectedIssueType.id}
                  label="Sub Type"
                  data={getSubIssueDropdown.data}
                  getIdValue={(val) => {
                    if (!val?.id) return;
                    setSelectedSubIssueType(val);
                  }}
                  getValueById={selectedSubIssueType?.id}
                  isStarRequired
                />
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <Button
            className="btn btn-secondary"
            onClick={() => setShowCloseModal(false)}
            disabled={isCloseInProgress}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-danger"
            onClick={submitCloseTicket}
            disabled={
              isCloseInProgress ||
              !closeMessage.trim() ||
              !selectedIssueType ||
              (getSubIssueDropdown?.data?.length > 0 && !selectedSubIssueType)
            }
          >
            {isCloseInProgress ? 'Closing…' : 'Close Ticket'}
          </Button>
        </div>
      </ModalPop>

      {/* ── Success Modal ── */}
      <ModalPop title={localization.success} show={isSuccess} handleModalHide={handleIsSuccess}>
        {successIconBox()}
      </ModalPop>

      {/* ── Reply success ── */}
      <ModalPop title={localization.success} show={isReply} handleModalHide={handleIsReply}>
        <div className="text-center">
          <div className="success"><img src={Images?.tick} alt="" /></div>
          <h5>Success</h5>
          <Button className="btn btn-primary mb-4" onClick={handleIsReply}>Close</Button>
        </div>
      </ModalPop>
    </Fragment>
  );
};

CentralTechViewIssue.propTypes = {
  fetchCteIssueList: propTypes.func,
};

export default CentralTechViewIssue;
